import Dialog from "@components/Dialog"
import localizeDate from "@helpers/localizeDate"
import {
  getSubscriptionState,
  SubscriptionState,
} from "@helpers/subscriptionState"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { RevenueCatSubscription } from "../types"

const STATUS_KEYS: Record<SubscriptionState, string> = {
  active: "pages.account.subscription.dialog.statusActive",
  canceled: "pages.account.subscription.dialog.statusCanceled",
  expired: "pages.account.subscription.dialog.statusExpired",
  refunded: "pages.account.subscription.dialog.statusRefunded",
}

interface Props {
  open: boolean
  onClose: () => void
  subscriptions: RevenueCatSubscription[]
}

export default function SubscriptionDialog({
  open,
  onClose,
  subscriptions,
}: Props) {
  const { t } = useTranslation()

  const expiresDate = (subscription: RevenueCatSubscription) =>
    new Date(subscription.expires_date)
  const purchaseDate = (subscription: RevenueCatSubscription) =>
    new Date(subscription.purchase_date) // unix timestamp in seconds, convert to milliseconds

  const store = (subscription: RevenueCatSubscription) => {
    switch (subscription.store) {
      case null:
      case undefined:
        return t("pages.account.subscription.dialog.unknown").toUpperCase()
      case "stripe":
        return "WEB"
      default:
        return subscription.store.split("_").join(" ").toUpperCase()
    }
  }

  const now = Date.now()
  const states = subscriptions.map((subscription) =>
    getSubscriptionState(subscription, now),
  )
  // NOTE: Only renewing subscriptions count: the warning is about being
  // charged twice, not about old, canceled or refunded ones in the history.
  const activeCount = states.filter((state) => state === "active").length
  const showMultipleSubscriptionsWarning = activeCount > 1

  return (
    <Dialog onClose={onClose} open={open}>
      <>
        <h1 className="vm-heading">
          {t("pages.account.subscription.dialog.header")}
        </h1>
        {showMultipleSubscriptionsWarning && (
          <p className="vm-note bg-vm-red-soft text-vm-red-text">
            {t("pages.account.subscription.dialog.multipleSubscriptions", {
              count: activeCount,
            })}
          </p>
        )}

        {subscriptions.map((subscription, index) => (
          <div
            key={`${subscription.store}-${subscription.purchase_date}`}
            className="overflow-hidden rounded-vm-md border border-vm-line"
          >
            <table className="w-full table-auto text-[15px]">
              <tbody className="divide-y divide-vm-line">
                <tr>
                  <th className="px-4 text-left font-medium text-vm-muted">
                    {t("pages.account.subscription.dialog.type")}
                  </th>

                  <td className="px-4 py-3 text-right font-semibold text-vm-ink">
                    {store(subscription)}
                  </td>
                </tr>
                <tr>
                  <th className="px-4 text-left font-medium text-vm-muted">
                    {t("pages.account.subscription.dialog.status")}
                  </th>

                  <td className="px-4 py-3 text-right font-semibold text-vm-ink">
                    {t(STATUS_KEYS[states[index]])}
                  </td>
                </tr>

                <tr>
                  <th className="px-4 text-left font-medium text-vm-muted">
                    {t("pages.account.subscription.dialog.begins")}
                  </th>
                  <td className="px-4 py-3 text-right font-semibold text-vm-ink">
                    {localizeDate(purchaseDate(subscription))}
                  </td>
                </tr>

                <tr>
                  <th className="px-4 text-left font-medium text-vm-muted">
                    {t(
                      states[index] === "expired" ||
                        states[index] === "refunded"
                        ? "pages.account.subscription.dialog.ended"
                        : "pages.account.subscription.dialog.ends",
                    )}
                  </th>

                  <td className="px-4 py-3 text-right font-semibold text-vm-ink">
                    {localizeDate(expiresDate(subscription))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
        <div className="pt-2 text-center">
          <Link
            className="vm-link text-sm"
            to="/help-center"
            data-testid="button-manage"
          >
            {t("pages.account.subscription.dialog.needHelpContactUs")}
          </Link>
        </div>
      </>
    </Dialog>
  )
}
